import ScrollingContainer from '@/components/shared/scrolling-container';
import { MOTION } from '@/utils';
import { withBasePath } from '@/utils/basePath';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import Image from 'next/image';
import { useRef } from 'react';

const CLIENT_LOGOS_ROW_ONE = [
  { alt: 'Azarmi Logo', src: withBasePath('/images/clients/azarmiLogo.webp') },
  { alt: 'Bonifassi Logo', src: withBasePath('/images/clients/bonifassiLogo.webp') },
  { alt: 'Ollyns Logo', src: withBasePath('/images/clients/ollynsLogo.webp') },
  { alt: 'Valther Logo', src: withBasePath('/images/clients/valtherLogo.webp') },
  { alt: 'Vinci Logo', src: withBasePath('/images/clients/vinciLogo.webp') },
  { alt: 'Vatier Logo', src: withBasePath('/images/clients/vatierLogo.webp') },
];

const CLIENT_LOGOS_ROW_TWO = [
  { alt: 'Bonifassi Logo', src: withBasePath('/images/clients/bonifassiLogo.webp') },
  { alt: 'La Martine Logo', src: withBasePath('/images/clients/laMartineLogo.webp') },
  { alt: 'Flichy Logo', src: withBasePath('/images/clients/flichyLogo.webp') },
  { alt: 'Lombard Logo', src: withBasePath('/images/clients/lombardLogo.webp') },
  { alt: 'Navacelle Logo', src: withBasePath('/images/clients/navacelleLogo.webp') },
  { alt: 'Ollyns Logo', src: withBasePath('/images/clients/ollynsLogo.webp') },
];

const rowClassName =
  "clients-row relative w-full overflow-hidden bg-white before:absolute before:left-0 before:top-0 before:z-2 before:h-full before:w-[100px] before:bg-[linear-gradient(to_right,white_0%,rgba(255,255,255,0)_100%)] before:content-[''] after:absolute after:right-0 after:top-0 after:z-2 after:h-full after:w-[100px] after:bg-[linear-gradient(to_left,white_0%,rgba(255,255,255,0)_100%)] after:content-['']";

function ClientLogoRow({
  logos,
  reverse = false,
}: {
  logos: Array<{ alt: string; src: string }>;
  reverse?: boolean;
}) {
  return (
    <ScrollingContainer
      className={`${rowClassName} ${reverse ? '-scale-x-100' : ''}`}
      scrollSpeed={18}
    >
      <div className="flex items-center gap-10 px-5 md:gap-16 md:px-8">
        {logos.map((logo) => (
          <Image
            key={`${reverse ? 'reverse' : 'default'}-${logo.src}`}
            alt={logo.alt}
            className={`h-20 w-auto object-contain grayscale transition-[filter] duration-300 hover:grayscale-0 md:h-28 ${reverse ? '-scale-x-100' : ''}`}
            decoding="async"
            height={80}
            loading="lazy"
            src={logo.src}
            width={160}
          />
        ))}
      </div>
    </ScrollingContainer>
  );
}

export default function Clients() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const titleRef = useRef<HTMLElement | null>(null);
  const rowsRef = useRef<HTMLDivElement[]>([]);

  useGSAP(
    () => {
      if (!sectionRef.current || !titleRef.current) return;

      const splitTitle = new SplitText(titleRef.current, {
        type: 'lines',
        mask: 'lines',
      });

      const tl = gsap.timeline({
        defaults: {
          duration: MOTION.durationMd,
          ease: MOTION.ease,
        },
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 70%',
          toggleActions: 'play none none reverse',
        },
      });

      tl.from(splitTitle.lines, {
        yPercent: MOTION.yPercent,
        stagger: MOTION.staggerLines,
      }).from(
        rowsRef.current,
        {
          autoAlpha: 0,
          y: MOTION.y,
          stagger: 0.15,
        },
        '<+0.2',
      );

      return () => {
        splitTitle.revert();
      };
    },
    { scope: sectionRef },
  );

  return (
    <section ref={sectionRef} className="max-width-white-section">
      <div className="max-width-white-section-content flex flex-col gap-8">
        <h2
          ref={(element) => {
            titleRef.current = element;
          }}
          className="text-orange!"
        >
          Ils nous font confiance
        </h2>

        <div
          ref={(element) => {
            if (element) rowsRef.current[0] = element;
          }}
        >
          <ClientLogoRow logos={CLIENT_LOGOS_ROW_ONE} />
        </div>
        <div
          ref={(element) => {
            if (element) rowsRef.current[1] = element;
          }}
        >
          <ClientLogoRow logos={CLIENT_LOGOS_ROW_TWO} reverse />
        </div>
      </div>
    </section>
  );
}
